/**
 * Zara Order JSON Import — pure helpers (NORMAL ZARA ONLY).
 *
 * Bu fazda amaç: Zara order JSON → parse / validate / normalize
 * → form alanları + newPriceLabel üretimi.
 *
 * ZaraHome bu akışa ASLA girmemelidir (brand === 'ZARA' zorunluluğu).
 *
 * Ekstra dependency YOK — sadece pure JS.
 */

/* ============================ Company name normalizer ==================== */

const TR_UPPER_TO_ASCII = {
  İ: 'I', I: 'I', ı: 'I', i: 'I',
  Ş: 'S', S: 'S', ş: 'S', s: 'S',
  Ğ: 'G', G: 'G', ğ: 'G', g: 'G',
  Ü: 'U', U: 'U', ü: 'U', u: 'U',
  Ö: 'O', O: 'O', ö: 'O', o: 'O',
  Ç: 'C', C: 'C', ç: 'C', c: 'C'
}

/**
 * Company isimleri için robust normalizer.
 * "BAYKAN DENIM KONFEKSIYON ANONIM SIRKETI" ile
 * "BAYKAN DENİM KONFEKSİYON ANONİM ŞİRKETİ" aynı sonucu verir.
 *
 * - TR/EN harf farkları (İ/I/ı/i, Ş/S, Ğ/G, Ü/U, Ö/O, Ç/C)
 * - case farkları
 * - çoklu whitespace
 * - noktalama işaretleri
 * - baş/son boşluk
 */
export function normalizeCompanyName(raw) {
  if (raw == null) return ''
  let s = String(raw)
    .trim()
    .toLocaleUpperCase('tr-TR') // Türkçe locale'de i→İ doğrusu için önce TR upper
  // TR özgü harfleri ASCII muadiline çevir (İ→I, Ş→S, ...)
  s = Array.from(s)
    .map((ch) => (TR_UPPER_TO_ASCII[ch] != null ? TR_UPPER_TO_ASCII[ch] : ch))
    .join('')
  // noktalama işaretlerini kaldır (boşluğa çevirme — A.S. → AS)
  s = s.replace(/[.,\\/#!$%^&*;:{}=\-_`~()[\]"'?<>@+|]/g, '')
  // whitespace normalize
  s = s.replace(/\s+/g, ' ').trim()
  return s
}

/**
 * Zara renk kodu: form şeması 3 hane bekler (örn. "83" → "083").
 * 3+ karakter varsa dokunulmaz; boşsa boş döner.
 */
export function padZaraColorCode(raw) {
  if (raw == null) return ''
  const s = String(raw).trim()
  if (!s) return ''
  if (s.length >= 3) return s
  return s.padStart(3, '0')
}

/* ============================ Component values lookup ==================== */

/**
 * componentValues[] array'ini hızlı lookup'a çevirir.
 * Örn: getComponentValue(cv, 'PRODUCT_MODEL', '840/326') → "0840"
 *
 * DİKKAT: POInformation.model = 840 ama PRODUCT_MODEL = "0840".
 * component lookup key = "840/326" (groupKey), articleNo = "0840/326".
 */
export function buildComponentLookup(componentValues) {
  const byName = new Map()
  for (const cv of Array.isArray(componentValues) ? componentValues : []) {
    if (!cv || typeof cv !== 'object' || !cv.name) continue
    if (!byName.has(cv.name)) byName.set(cv.name, cv)
  }
  return {
    get: (name) => byName.get(name) || null,
    /** name + groupKey ile valueMap değerini döner */
    getValue: (name, groupKey) => {
      const cv = byName.get(name)
      if (!cv || !cv.valueMap) return null
      return cv.valueMap[groupKey] ?? null
    },
    /** name + groupKey ile metadata değerini döner */
    getMetadata: (name, groupKey) => {
      const cv = byName.get(name)
      if (!cv || !cv.metadata) return null
      return cv.metadata[groupKey] ?? null
    },
    /** SIZE group'lu component'lerin valueMap'ini (size → value) döner */
    getSizeMap: (name) => {
      const cv = byName.get(name)
      if (!cv || !cv.valueMap) return {}
      return cv.valueMap
    }
  }
}

/* ============================ Fiyat helpers ==================== */

/** "69,90" → 6990 ; "39.99" → 3999 ; null/bozuk → null (FLOAT KULLANMA) */
export function parseAmountMinor(sourceValue) {
  if (sourceValue == null) return null
  // Virgül ve nokta karışık olabilir; ondalık ayırıcıyı normalize et
  const s = String(sourceValue).trim().replace(/[^0-9.,-]/g, '')
  if (!s) return null
  // Son ayırıcıyı ondalık kabul et
  const normalized = s.replace(',', '.')
  const parts = normalized.split('.')
  if (parts.length > 2) {
    // 1.234,56 gibi — son nokta ondalık
    const last = parts.pop()
    return parseInt(parts.join('') + last, 10)
  }
  const n = Number(normalized)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100)
}

const CURRENCY_SYMBOL_TO_CODE = {
  $: 'USD', '€': 'EUR', '£': 'GBP', 'R$': 'BRL', 'AUD$': 'AUD', 'CLP$': 'CLP'
}

function normalizePriceEntry(currencySymbol, sourceValue, role) {
  const symbol = currencySymbol != null ? String(currencySymbol).trim() : ''
  const currencyCode = CURRENCY_SYMBOL_TO_CODE[symbol] || symbol || null
  const amountMinor = parseAmountMinor(sourceValue)
  return {
    role,
    currencyCode,
    symbol: symbol || null,
    sourceValue: sourceValue != null ? String(sourceValue) : null,
    amountMinor
  }
}

/* ============================ Zaman/ID helpers ==================== */

function sizeKey(size) {
  return String(size).trim()
}

/**
 * Beden karşılaştırma anahtarı — sıfır dolgusuna dayanıklı:
 *   "1" === "01" === "1 " ; "9-10" === "09-10" değil (aralık eşitliği korunur)
 * Harf bedenlerde ("XS") trim yeterli.
 */
function sizeMatchKey(size) {
  const s = String(size ?? '').trim()
  // Tamamen sayısal ise baştaki sıfırları at
  if (/^\d+$/.test(s)) return String(parseInt(s, 10))
  return s
}

/* ============================ Ana normalizer ==================== */

/**
 * Zara order JSON'undan (source of truth: order_41319_supplier_11667_variableData JSON)
 * normalize edilmiş newPriceLabel base object'i üretir.
 *
 * Dönen obje QR cache SONUCUNU İÇERMEZ — qr alanları backend cache-qr
 * çağrısından sonra ayrıca doldurulur (cacheStatus: 'pending' ile başlar).
 *
 * @returns {{ ok: boolean, errors: string[], warnings: string[], data: object|null }}
 */
export function normalizeZaraOrderJson(rawJson) {
  const errors = []
  const warnings = []

  /* ---------- 1. Base schema validate ---------- */
  if (!rawJson || typeof rawJson !== 'object') {
    return { ok: false, errors: ['JSON içeriği boş veya obje değil.'], warnings, data: null }
  }

  const po = rawJson.POInformation
  if (!po || typeof po !== 'object') {
    errors.push("Zorunlu alan 'POInformation' bulunamadı.")
  }

  const supplier = rawJson.supplier
  if (!supplier || typeof supplier !== 'object') {
    errors.push("Zorunlu alan 'supplier' bulunamadı.")
  }

  if (!Array.isArray(po?.colors) || po.colors.length === 0) {
    errors.push("'POInformation.colors' array'i yok veya boş.")
  }

  /* ---------- 2. Brand safety check ---------- */
  // Kabul edilen markalar: normal ZARA + ZARA SUR (South America / -25 order'lar).
  // ZaraHome hâlâ REDD EDİLİR — bu importer onun için değil.
  const brandRaw = String(po?.brand ?? '').trim().toUpperCase()
  const ALLOWED_BRANDS = ['ZARA', 'ZARA SUR']
  if (!ALLOWED_BRANDS.includes(brandRaw)) {
    errors.push(
      `JSON brand desteklenmiyor ("${brandRaw || 'boş'}"). Bu importer sadece normal Zara ve ZARA SUR (South America) içindir; ZaraHome JSON'u kabul edilmez.`
    )
  }

  // Hatalar varsa form mutate ETMEDEN dur
  if (errors.length > 0) return { ok: false, errors, warnings, data: null }

  /* ---------- 3. Component normalize ---------- */
  const cv = buildComponentLookup(rawJson.componentValues)
  const componentGroupKey = `${String(po.model)}/${String(po.quality)}` // "840/326"

  const productModelRaw = cv.getValue('PRODUCT_MODEL', componentGroupKey)
  const productQualityRaw = cv.getValue('PRODUCT_QUALITY', componentGroupKey)

  const model =
    productModelRaw != null
      ? String(productModelRaw)
      : String(po.model).padStart(4, '0') // PRODUCT_MODEL yoksa POInformation.model'i 4 haneye pad'le
  const quality =
    productQualityRaw != null ? String(productQualityRaw) : String(po.quality)
  const articleNo = `${model}/${quality}` // "0840/326"

  const productRef = cv.getValue('PRODUCT_REF', componentGroupKey)
  const productColor = cv.getValue('PRODUCT_COLOR', componentGroupKey)
  // Beden serisi: normal ZARA'da SIZE_SERIE, ZARA SUR (Kids) JSON'larında
  // SIZE_SERIE_AGE ("6-7,8-9,...") kullanılır — fallback sırasıyla ikisini de dene.
  const sizeSerieRaw =
    cv.getValue('SIZE_SERIE', componentGroupKey) ??
    cv.getValue('SIZE_SERIE_AGE', componentGroupKey)

  const sizeSerie = sizeSerieRaw
    ? String(sizeSerieRaw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  if (!/^\d+$/.test(model) || !/^\d+$/.test(quality)) {
    errors.push(`Model/quality çıkarılamıyor veya sayısal değil (model: "${model}", quality: "${quality}").`)
    return { ok: false, errors, warnings, data: null }
  }

  /* ---------- 4. Prices ---------- */
  const prices = {
    usa: normalizePriceEntry(
      cv.getValue('PRICE_USA_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_USA_VALUE', componentGroupKey),
      'MAIN_USA'
    ),
    uk: normalizePriceEntry(
      cv.getValue('PRICE_UK_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_UK_VALUE', componentGroupKey),
      'MAIN_UK'
    ),
    black: normalizePriceEntry(
      cv.getValue('PRICE_BLACK_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_BLACK_VALUE', componentGroupKey),
      'PIGGYBACK_BLACK'
    ),
    // Piggyback kartları (index.jsx yan kartları):
    //   RED  → üst kart (DynamicTextLeftt top={true}  → legacy mix[0])
    //   BLUE → alt kart (DynamicTextLeftt top={false} → legacy mix[1])
    red: normalizePriceEntry(
      cv.getValue('PRICE_RED_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_RED_VALUE', componentGroupKey),
      'PIGGYBACK_RED'
    ),
    blue: normalizePriceEntry(
      cv.getValue('PRICE_BLUE_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_BLUE_VALUE', componentGroupKey),
      'PIGGYBACK_BLUE'
    ),
    // South America ana kart fiyatı: "R$ 85" + "Exclusivo para Brasil"
    br: normalizePriceEntry(
      cv.getValue('PRICE_BR_CURRENCY', componentGroupKey),
      cv.getValue('PRICE_BR_VALUE', componentGroupKey),
      'MAIN_BR'
    )
  }

  /* ---------- 5. Template / labels ---------- */
  const mainLabel = Array.isArray(rawJson.labels) ? rawJson.labels[0] : null
  const piggybackBlackLabel = mainLabel?.childrenLabels?.find(
    (cl) => String(cl?.reference ?? '').trim().toUpperCase() === 'PIGGYBACK_BLACK'
  )

  const template = {
    reference: mainLabel?.reference ?? null, // "HPZCALL004"
    maestroetiRef: mainLabel?.maestroetiRef ?? null,
    buyerGroupIconUrl: cv.getValue('ICON_BUYER_GROUP', componentGroupKey)
  }

  /* ---------- 6. Geographic size maps ---------- */
  const geoComponents = {
    EUR: 'SIZE_GEOGRAPHIC_EUR',
    BR: 'SIZE_GEOGRAPHIC_BR',
    MEX: 'SIZE_GEOGRAPHIC_MEX',
    USA: 'SIZE_GEOGRAPHIC_USA',
    UK: 'SIZE_GEOGRAPHIC_UK',
    IT: 'SIZE_GEOGRAPHIC_IT'
  }
  const sizeCodeMap = cv.getSizeMap('SIZE_CODE')
  const geoMaps = {}
  for (const [geoKey, componentName] of Object.entries(geoComponents)) {
    const map = cv.getSizeMap(componentName)
    if (map && Object.keys(map).length > 0) geoMaps[geoKey] = map
  }

  /* ---------- 6b. Kids ölçü map'leri (ZARA SUR) ---------- */
  // SIZE_MEASURE_AGE:     "10" → "9-10" (beden → yaş aralığı)
  // SIZE_MEASURE_HEIGHT_CM: "10" → "140" (beden → boy cm)
  const ageMap = cv.getSizeMap('SIZE_MEASURE_AGE')
  const heightCmMap = cv.getSizeMap('SIZE_MEASURE_HEIGHT_CM')

  /* ---------- 7. PRODUCT_QR valueMap + metadata ---------- */
  // groupKey iki biçimde gelir:
  //   SIZE       → key'ler beden:        { "38": url }          (yetişkin JSON'ları)
  //   COLOR_SIZE → key'ler "renk/beden": { "250/10": url }      (ZARA SUR / Kids)
  // COLOR_SIZE'te QR payload'ı renk+beden içerdiği için (örn. ...728 250 10)
  // lookup RENK BAZLI yapılır — flatten edilmez, aksi halde 250/10'un QR'si
  // 620/10'a yanlış atanırdı. Aşağıda sizes.push içinde iki adımlı lookup var.
  const qrValueMap = cv.getSizeMap('PRODUCT_QR')
  const qrMetadata = (cv.get('PRODUCT_QR')?.metadata) || {}

  /**
   * COLOR_SIZE ("250/10") → SIZE ("10") iki adımlı QR lookup.
   * Ham ("83") ve pad'li ("083") key'lerin ikisini de dener.
   */
  const qrLookup = (map, rawColor, sz) => {
    const padded = padZaraColorCode(rawColor)
    return (
      map[`${rawColor}/${sz}`] ??
      (padded !== rawColor ? map[`${padded}/${sz}`] : undefined) ??
      map[sz] ??
      null
    )
  }

  const instanceLookup = (valueMap, rawColor, sz) => {
    const padded = padZaraColorCode(rawColor)
    return (
      valueMap[`${rawColor}/${sz}`] ??
      (padded !== rawColor ? valueMap[`${padded}/${sz}`] : undefined) ??
      null
    )
  }

  /* ---------- 8. Variants (multi-color hazır) ---------- */
  const variants = []
  let calculatedOrderQty = 0

  for (const colorEntry of po.colors) {
    const rawColor = String(colorEntry?.color ?? '').trim()
    // Form / etiket: minimum 3 hane ("83" → "083")
    const colorCode = padZaraColorCode(rawColor)
    const variantGroupId = colorEntry?.variantGroupId ?? null
    if (!colorCode) {
      warnings.push("Bir color entry'de color kodu yok; atlandı.")
      continue
    }
    const labelInstances = mainLabel?.labelInstances?.valueMap || {}
    const piggybackInstances = piggybackBlackLabel?.labelInstances?.valueMap || {}

    const sizes = []
    let variantQty = 0

    for (const sizeEntry of colorEntry.sizes || []) {
      const sz = sizeKey(sizeEntry?.size)
      const qty = Number(sizeEntry?.qty)
      if (!sz || !Number.isFinite(qty)) {
        warnings.push(`Renk ${colorCode}: geçersiz beden/adet girişi atlandı (size: ${sz}, qty: ${sizeEntry?.qty}).`)
        continue
      }
      variantQty += qty

      sizes.push({
        size: sz,
        // SIZE_CODE map değeri ("2" → "02") — productLine bu kodu basar
        code: sizeCodeMap[sz] ?? null,
        qty,
        referenceId: sizeEntry?.referenceId ?? null,
        geographic: {
          EUR: geoMaps.EUR?.[sz] ?? sizeCodeMap[sz] ?? null,
          BR: geoMaps.BR?.[sz] ?? null,
          MEX: geoMaps.MEX?.[sz] ?? null,
          USA: geoMaps.USA?.[sz] ?? null,
          UK: geoMaps.UK?.[sz] ?? null,
          IT: geoMaps.IT?.[sz] ?? null
        },
        // Kids ölçüleri (ZARA SUR): yaş aralığı ve boy cm — yoksa null
        age: ageMap[sz] ?? null, // "10" → "9-10"
        heightCm: heightCmMap[sz] ?? null, // "10" → "140"
        qr: {
          // COLOR_SIZE: önce "renk/beden" key'i dene, yoksa beden-only (yetişkin)
          payload: qrLookup(qrMetadata, rawColor, sz) ?? null, // stable zara.com/qr URL
          originalUrl: qrLookup(qrValueMap, rawColor, sz) ?? null, // signed Azure URL (ephemeral)
          cacheKey: null,
          localPath: null,
          cacheStatus: 'pending'
        },
        labelInstanceId: instanceLookup(labelInstances, rawColor, sz),
        piggybackBlackInstanceId: instanceLookup(piggybackInstances, rawColor, sz)
      })
    }

    calculatedOrderQty += variantQty
    variants.push({
      colorCode,
      variantGroupId,
      orderedQty: variantQty,
      sizes
    })
  }

  if (variants.length === 0) {
    errors.push('Geçerli renk/beden kombinasyonu bulunamadı.')
    return { ok: false, errors, warnings, data: null }
  }

  /* ---------- 9. Qty validation ---------- */
  const declaredOrderQty = Number(po.orderQty)
  const quantityMatches =
    Number.isFinite(declaredOrderQty) && declaredOrderQty === calculatedOrderQty
  if (!quantityMatches) {
    warnings.push(
      `Sipariş adedi uyuşmuyor: JSON orderQty = ${declaredOrderQty}, bedenlerden hesaplanan toplam = ${calculatedOrderQty}.`
    )
  }

  /* ---------- 10. newPriceLabel base ---------- */
  const data = {
    schemaVersion: 1,
    provider: 'INDITEX',
    brand: 'ZARA',

    source: {
      productionOrderNumber: String(po.productionOrderNumber ?? ''),
      campaign: String(po.campaign ?? ''),
      orderQty: declaredOrderQty,
      brand: brandRaw,
      section: String(po.section ?? ''), // "MAN"
      productType: String(po.productType ?? ''), // "CLOTHES"

      supplier: {
        supplierCode: String(supplier.supplierCode ?? ''),
        name: String(supplier.name ?? '')
      }
    },

    article: {
      model, // "0840"
      quality, // "326"
      articleNo, // "0840/326"
      productRef, // "1-54SRM"
      productColor, // "428" (tek renk bilgisi; çok renk variants'ta)
      sizeSerie, // ["34","36",...] — ZARA SUR'da SIZE_SERIE_AGE'den: ["6-7","8-9",...]
      ageUnit: cv.getValue('SIZE_MEASURE_AGE_UNIT', componentGroupKey) ?? null // "AÑOS/YEARS" (Kids), bazen ay olabilir
    },

    template,

    prices,

    variants,

    validation: {
      declaredOrderQty,
      calculatedOrderQty,
      quantityMatches
    }
  }

  return { ok: true, errors: [], warnings, data }
}

/* ============================ Legacy priceLabel mirror ==================== */

/**
 * newPriceLabel.prices'ı legacy priceLabel[0].mix'e aynalar.
 *
 * SelectCurrency slot kuralları (components/form/compobox/select-priceand-unit.jsx):
 *   mix[0] → EUR/AUD  ← YENİ ZARA İMPORTUNDA BOŞ (BLACK legacy'ye yazılmaz)
 *   mix[1] → EUR/CLP  ← boş bırak
 *   mix[2] → USD/BRL  ← PRICE_USA  (USD $)
 *   mix[3] → GBP      ← PRICE_UK   (GBP £)
 *
 * Fiyat string formatı: form "69.90" bekliyor (virgül → nokta).
 *
 * NOT: Bu mirror AUTHORITATIVE'dır — JSON import her seferinde mix'i taze kurar.
 * mix[0]/mix[1] EXPLICIT boşaltılır; eski importlardan (örn. BLACK'in hâlâ
 * mix[0]'a yazıldığı dönemden) kalan kalıntı EUR değerleri temizlenir.
 * "Boş slotları koru" davranışı bilinçli olarak kaldırıldı — aksi halde eski
 * BLACK kalıntısı ekranda 39.95 EUR olarak görülmeye devam eder.
 *
 * @param {object} newPriceLabel normalizeZaraOrderJson çıktısı
 * @param {object[]} existingMix legacy priceLabel[0].mix (kullanılmaz — sadece
 *   slot shape'i korunur)
 */
export function mirrorPricesToLegacyMix(newPriceLabel, existingMix) {
  // Slot shape'i mevcut mix'ten al ama değerleri TAMAMEN sıfırla
  const base = (Array.isArray(existingMix) && existingMix.length === 4
    ? existingMix
    : [{}, {}, {}, {}]
  ).map(() => ({ currency: '', type: '' }))

  const toLegacyPrice = (p) => {
    if (p?.sourceValue == null) return { currency: '', type: '' }
    return {
      currency: String(p.sourceValue).replace(',', '.'), // "69,90" → "69.90"
      type: p.currencyCode || ''
    }
  }

  // NOT: PRICE_BLACK (PIGGYBACK_BLACK) bilinçli olarak legacy mix'e YAZILMAZ.
  // Yeni Zara importunda legacy main price card sadece USA + UK taşır.
  // BLACK semantik verisi newPriceLabel.prices.black içinde authoritative kalır.
  const usa = toLegacyPrice(newPriceLabel?.prices?.usa)
  const uk = toLegacyPrice(newPriceLabel?.prices?.uk)

  // PIGGYBACK kartları: JSON'da PRICE_RED/PRICE_BLUE varsa yan kartlara yaz.
  //   RED  → mix[0] (üst kart — DynamicTextLeftt top={true})
  //   BLUE → mix[1] (alt kart — DynamicTextLeftt top={false})
  // JSON'da yoksa slotlar boş kalır → manuel senaryolar ezilmez.
  const red = toLegacyPrice(newPriceLabel?.prices?.red)
  const blue = toLegacyPrice(newPriceLabel?.prices?.blue)

  // mix index semantiği KORUNUR: mix[0]=EUR/AUD, mix[1]=EUR/CLP, mix[2]=USD/BRL, mix[3]=GBP
  if (red.currency) base[0] = { ...base[0], ...red }
  if (blue.currency) base[1] = { ...base[1], ...blue }
  if (usa.currency) base[2] = { ...base[2], ...usa }
  if (uk.currency) base[3] = { ...base[3], ...uk }

  return base
}

/* ============================ Form alanları mapping ==================== */

/**
 * JSON'dan Formik top-level alanlarına mapping.
 * orderNo / country / customerOrderNo / changesForEveryProduct / RFID DOKUNULMAZ.
 *
 * brandOrderNo eki: JSON brand'i "ZARA SUR" ise sona "-25" (South America),
 * normal "ZARA" ise "-D" eklenir. Örn: 69588-25 / 43719-D.
 */
export function zaraJsonToFormFields(newPriceLabel) {
  const pon = newPriceLabel?.source?.productionOrderNumber
  const brandRaw = String(newPriceLabel?.source?.brand ?? '').trim().toUpperCase()
  const suffix = brandRaw === 'ZARA SUR' ? '-25' : '-D'

  return {
    brandOrderNo: pon ? `${String(pon)}${suffix}` : '',
    supplierNo: newPriceLabel?.source?.supplier?.supplierCode
      ? String(newPriceLabel.source.supplier.supplierCode)
      : '',
    season: newPriceLabel?.source?.campaign || '',
    articleNo: newPriceLabel?.article?.articleNo || ''
  }
}

/* ============================ Beden tablosu eşleştirme ==================== */

/**
 * JSON sizeSerie ile katalog beden tablolarını eşleştirir.
 *
 * Gerçek katalog tabloları JSON serisinin ÜST KÜMESİ olabilir:
 *   JSON seri:  [34,36,38,40,42,44,46]
 *   ERKEK ALT - RAKAMLI: [34..62]  → tüm JSON bedenlerini kapsar (superset)
 *
 * Eşleşme stratejisi:
 *   1. EXACT: visible size set == JSON seri set (tek ise seç)
 *   2. SUPERSET: JSON serisi tablonun ALT KÜMESİ olanlar arasından
 *      en dar kapsam (fazla bedeni en az) olanı seç — yanlış geniş tabloyu
 *      seçmemek için. Birden fazla eşit aday varsa seçme (warning).
 *   3. Hiçbiri: null — mevcut tablo korunur, warning importer'da verilir.
 *
 * @param {string[]} serie JSON sizeSerie (örn. ['34','36','38','40','42','44','46'])
 * @param {Array<{id:string, name:string, sizes:Array<{europe?:number|string, sCode?:string}>}>} groups
 * @returns {{ match: object|null, strategy: 'exact'|'superset'|null, ambiguous: object[] }}
 */
export function matchSizeGroupBySerie(serie, groups) {
  if (!Array.isArray(serie) || serie.length === 0) {
    return { match: null, strategy: null, ambiguous: [] }
  }

  const norm = (s) => String(s).trim()
  const setOf = (arr) => new Set(arr.map(norm))
  const keyOf = (set) => Array.from(set).sort((a, b) => Number(a) - Number(b)).join(',')

  const targetSet = setOf(serie)
  const targetKey = keyOf(targetSet)

  const candidates = []
  for (const g of groups || []) {
    const sizes = Array.isArray(g?.sizes) ? g.sizes : []
    if (sizes.length === 0) continue
    const visible = sizes.map((s) =>
      s?.europe != null ? String(s.europe) : String(s?.sCode ?? '')
    )
    const visibleSet = setOf(visible.filter(Boolean))
    if (visibleSet.size === 0) continue

    // Tüm JSON bedenleri tabloda var mı? (JSON set ⊆ tablo set)
    let coversAll = true
    for (const t of targetSet) {
      if (!visibleSet.has(t)) {
        coversAll = false
        break
      }
    }
    if (!coversAll) continue

    candidates.push({
      group: g,
      setSize: visibleSet.size,
      exact: keyOf(visibleSet) === targetKey
    })
  }

  // 1) Exact eşitlik
  const exacts = candidates.filter((c) => c.exact)
  if (exacts.length === 1) return { match: exacts[0].group, strategy: 'exact', ambiguous: [] }
  if (exacts.length > 1) return { match: null, strategy: null, ambiguous: exacts.map((c) => c.group) }

  // 2) Superset — en dar kapsamlı tablo
  const supersets = candidates.filter((c) => !c.exact)
  if (supersets.length === 0) return { match: null, strategy: null, ambiguous: [] }

  const minSize = Math.min(...supersets.map((c) => c.setSize))
  const best = supersets.filter((c) => c.setSize === minSize)
  if (best.length === 1) return { match: best[0].group, strategy: 'superset', ambiguous: [] }
  return { match: null, strategy: null, ambiguous: best.map((c) => c.group) }
}

/* ============================ Beden eşleştirme ==================== */

/**
 * JSON ordered size'larını mevcut form size satırlarına eşler.
 * Form satır formatı (PriceSize/index.js):
 *   { name, amount, amountWithDefect, cn, cm, uid, sCode }
 *
 * Eşleşme önceliği (matchCatalogSizes yaklaşımı ile uyumlu):
 *   1. sCode birebir
 *   2. europe / visible size normalize
 *
 * Sipariş edilmeyen serideki bedenler: amount '' kalır.
 */
export function applyQuantitiesToFormSizes(formSizes, variant) {
  if (!Array.isArray(formSizes) || !variant?.sizes) return formSizes

  const qtyBySize = new Map()
  for (const s of variant.sizes) {
    qtyBySize.set(sizeKey(s.size), s.qty)
    // "1" ↔ "01" gibi sıfır dolgulu tablo sCode'ları için normalize anahtar
    qtyBySize.set(sizeMatchKey(s.size), s.qty)
  }

  return formSizes.map((row) => {
    const sCode = String(row?.sCode ?? '').trim()
    const name = String(row?.name ?? '').trim()
    const qty =
      qtyBySize.get(sCode) ??
      qtyBySize.get(name) ??
      qtyBySize.get(sizeMatchKey(sCode)) ??
      qtyBySize.get(sizeMatchKey(name)) ??
      null
    return {
      ...row,
      amount: qty != null ? String(qty) : ''
    }
  })
}

/* ============================ QR cache istek listesi ==================== */

/**
 * newPriceLabel'dan backend cache-qr isteği için items listesi üretir.
 * Dedupe: aynı payload/cacheKey bir kez gönderilir.
 */
export function buildQrCacheItems(newPriceLabel) {
  const items = []
  const seen = new Set()

  for (const variant of newPriceLabel?.variants || []) {
    for (const size of variant.sizes || []) {
      const url = size?.qr?.originalUrl
      const payload = size?.qr?.payload
      if (!url && !payload) continue
      const key = payload || url
      if (seen.has(key)) continue
      seen.add(key)
      items.push({
        size: size.size,
        referenceId: size.referenceId,
        url,
        payload
      })
    }
  }
  return items
}

/**
 * Backend cache-qr cevabıyla newPriceLabel.variants[].sizes[].qr alanlarını tamamlar.
 * Yeni obje döner (mutate etmez).
 */
export function applyQrCacheResults(newPriceLabel, results) {
  if (!Array.isArray(results)) return newPriceLabel

  // cacheKey lookup: payload veya url üzerinden
  const byPayload = new Map()
  const byUrl = new Map()
  for (const r of results) {
    if (r?.payload) byPayload.set(r.payload, r)
    if (r?.originalUrl) byUrl.set(r.originalUrl, r)
  }

  return {
    ...newPriceLabel,
    variants: newPriceLabel.variants.map((variant) => ({
      ...variant,
      sizes: variant.sizes.map((size) => {
        const r =
          (size.qr?.payload && byPayload.get(size.qr.payload)) ||
          (size.qr?.originalUrl && byUrl.get(size.qr.originalUrl)) ||
          null
        if (!r) return size
        return {
          ...size,
          qr: {
            ...size.qr,
            cacheKey: r.cacheKey ?? null,
            localPath: r.localPath ?? null,
            cacheStatus: r.cacheStatus === 'cached' ? 'cached' : 'failed',
            cacheError: r.cacheStatus === 'cached' ? undefined : r.reason || 'Bilinmeyen hata'
          }
        }
      })
    }))
  }
}

/* ============================ Department safety check ==================== */

const SECTION_TO_DEPARTMENT_HINT = {
  MAN: ['MAN', 'ERKEK'],
  WOMAN: ['WOMAN', 'KADIN', 'WOMEN'],
  KID: ['KID', 'ÇOCUK', 'KIDS'],
  BABY: ['BABY', 'BEBEK'],
  // ZARA SUR (South America) JSON'ları section olarak "CHILDREN" kullanır
  CHILDREN: ['KID', 'ÇOCUK', 'KIDS', 'CHILDREN', 'CHILD']
}

/**
 * JSON section ("MAN") ile formdaki Department context karşılaştırması.
 * Uyum yoksa import durdurulur.
 *
 * DİKKAT: "WOMAN" string'i "MAN" içerir — substring kontrol yerine
 * kelime bazlı eşleşme kullanılır.
 */
export function checkSectionMatchesDepartment(section, departmentName) {
  const sectionNorm = String(section || '').trim().toUpperCase()
  const deptNorm = normalizeCompanyName(departmentName)

  if (!sectionNorm) return { ok: true } // JSON'da section yoksa dokunma
  if (!deptNorm) return { ok: true } // form context belirsizse dokunma

  const hints = SECTION_TO_DEPARTMENT_HINT[sectionNorm]
  if (!hints) return { ok: true } // bilinmeyen section → bariz çelişki yok

  // Kelime bazlı eşleşme: "BAYKAN MAN 34 46" → ["BAYKAN","MAN",...]
  const deptWords = deptNorm.split(' ').filter(Boolean)
  const matches = hints.some((h) => {
    const hintWords = normalizeCompanyName(h).split(' ').filter(Boolean)
    // Herhangi bir hint kelimesi department kelimelerinden biriyle birebir eşleşmeli
    return hintWords.some((hw) => deptWords.includes(hw))
  })
  if (matches) return { ok: true }

  return {
    ok: false,
    reason: `JSON section (${sectionNorm}) ile form Department (${departmentName}) uyuşmuyor. Import durduruldu.`
  }
}

/* ============================ Form override tespiti ==================== */

/**
 * Import öncesi formda dolu alanları tespit eder (overwrite confirmation için).
 */
export function detectOverwriteTargets(values) {
  const targets = []
  const fields = ['brandOrderNo', 'supplierNo', 'season', 'articleNo', 'company', 'sizeGroup']
  const labels = {
    brandOrderNo: 'Marka Order No',
    supplierNo: 'Supplier No',
    season: 'Sezon',
    articleNo: 'Artıkel',
    company: 'Müşteri Adı',
    sizeGroup: 'Beden Tablosu'
  }
  for (const f of fields) {
    if (values?.[f] != null && String(values[f]).trim() !== '') {
      targets.push({ field: f, label: labels[f] })
    }
  }
  if (values?.priceLabel?.[0]?.colorCode) {
    targets.push({ field: 'priceLabel[0].colorCode', label: 'Renk Kodu' })
  }
  // MULTI-COLOR: import tüm priceLabel array'ini yeniden kurar — ek renkler de uyarıda görünür
  const extraColors = (values?.priceLabel?.length || 0) - 1
  if (extraColors > 0) {
    targets.push({ field: 'priceLabel', label: `Mevcut ${extraColors + 1} renk girişi` })
  }
  if (values?.priceLabel?.[0]?.sizes?.some((s) => s?.amount)) {
    targets.push({ field: 'priceLabel[0].sizes', label: 'Beden Adetleri' })
  }
  if (values?.priceLabel?.[0]?.mix?.some((m) => m?.currency)) {
    targets.push({ field: 'priceLabel[0].mix', label: 'Fiyatlar' })
  }
  if (values?.newPriceLabel) {
    targets.push({ field: 'newPriceLabel', label: 'Önceki JSON import verisi' })
  }
  return targets
}
